import * as React from 'react';
const { connect } = require('react-redux');
import * as moment from 'moment';
import { translate } from 'react-i18next';
import { ResponsiveLine } from '@nivo/line';
import * as c from 'models/constants';
import { PageTitle, PortalPageSection } from 'components/PortalStructure';
import { DashboardOrders, ManageShopWrapper } from 'containers';
import { IStore } from 'redux/IStore';
import { IShopDashboardData } from 'models/shop/dashboard';
import { Periodicity } from 'models/enums/periodicity';
import {
	setPeriodicity,
	setPeriodForward,
	setPeriodBackwards,
	getSales,
	setShopId,
	resetData,
	getFollowers,
	getShopOrders,
	changeTabAndGetRequiredData,
} from 'redux/modules/shop/dashboard';
import { PeriodSelector } from 'components/Common/PeriodSelector';
import { LoadingOverlayWrapper } from 'components';
import { IModalAction } from 'models/modal';
import { OrderStatus } from 'models';
import { IOrdersAction } from 'models/orders/orders';
import { DashboardProducts } from '../DashboardProducts';
import { setStatus } from 'redux/modules/shop/orders';
import { closeModal, showModal } from 'redux/modules/modal';

require('./style.css');

interface IProps {
	params?: IParams;
	shopDashboardData: IShopDashboardData;
	setPeriodicity: (periodicity: Periodicity) => void;
	setShopId: (shopId: string) => void;
	setPeriodBackwards: () => void;
	setPeriodForward: () => void;
	getSales: (shopId: string) => void;
	getFollowers: (shopId: string) => void;
	getShopOrders: (shopId: string) => void;
	t?: (key: string, object?: any) => string;
	resetData?: () => void;
	showModal?: (modalType: string, modalProps: any) => IModalAction;
	closeModal?: () => IModalAction;
	setStatus?: (shopOrderId: string, shopId: string, status: OrderStatus, note: string) => IOrdersAction;
	changeTabAndGetRequiredData?: (selectedTab: string, shopId: string) => void;
}

interface IParams {
	id?: string;
}

@connect(
	(state: IStore) => ({
		shopDashboardData: state.shopDashboard,
	}),
	(dispatch) => ({
		setPeriodicity: (periodicity: Periodicity) => dispatch(setPeriodicity(periodicity, 'dashboard')),
		setPeriodBackwards: () => dispatch(setPeriodBackwards()),
		setPeriodForward: () => dispatch(setPeriodForward()),
		getSales: (shopId: string) => dispatch(getSales(shopId)),
		getFollowers: (shopId: string) => dispatch(getFollowers(shopId)),
		getShopOrders: (shopId: string) => dispatch(getShopOrders(shopId)),
		setShopId: (shopId: string) => dispatch(setShopId(shopId)),
		resetData: () => dispatch(resetData()),
		changeTabAndGetRequiredData: (selectedTab: string, shopId: string) =>
			dispatch(changeTabAndGetRequiredData(selectedTab, shopId)),
		setStatus: (shopOrderId: string, shopId: string, status: OrderStatus, note: string) =>
			dispatch(setStatus(shopOrderId, shopId, status, note)),
		closeModal: () => dispatch(closeModal()),
		showModal: (modalType: string, modalProps: any) => dispatch(showModal(modalType, modalProps)),
	}),
)
@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class ShopDashboard extends React.Component<IProps, {}> {
	constructor(props) {
		super(props);

		this.handlePeriodicityChange = this.handlePeriodicityChange.bind(this);
		this.handleOnBackwardsPeriodClick = this.handleOnBackwardsPeriodClick.bind(this);
		this.handleOnForwardPeriodClick = this.handleOnForwardPeriodClick.bind(this);
		this.changeTabAndGetRequiredData = this.changeTabAndGetRequiredData.bind(this);
	}

	public componentDidMount() {
		this.props.setShopId(this.props.params.id);
		this.props.getSales(this.props.params.id);
		this.props.getShopOrders(this.props.params.id);
		this.props.changeTabAndGetRequiredData(c.SHOPDASHBOARD_TAB_MOSTSOLD, this.props.params.id);
		this.props.getFollowers(this.props.params.id);
	}

	public componentWillUnmount() {
		this.props.resetData();
	}

	private handlePeriodicityChange(val: Periodicity) {
		this.props.setPeriodicity(val);
	}

	private handleOnBackwardsPeriodClick() {
		this.props.setPeriodBackwards();
	}

	private handleOnForwardPeriodClick() {
		this.props.setPeriodForward();
	}

	private prepareScale(selectedPeriodicity: Periodicity, dateFrom: string, items: any) {
		const array = [];
		const date = moment.utc(dateFrom);
		if (selectedPeriodicity === Periodicity.Week) {
			array.push(date.toDate());
			for (let i = 1; i <= 6; i++) {
				array.push(date.add(1, 'days').toDate());
			}
		} else if (selectedPeriodicity === Periodicity.Month) {
			array.push(date.toDate());
			for (let i = 1; i < date.daysInMonth(); i++) {
				array.push(date.add(1, 'days').toDate());
			}
		} else if (selectedPeriodicity === Periodicity.Year) {
			for (let i = 1; i <= 12; i++) {
				array.push(i);
			}
		} else if (selectedPeriodicity === Periodicity.AllTime) {
			items.map((y) => {
				if (!array.includes(moment.utc(y.date).toDate().getFullYear())) {
					array.push(moment.utc(y.date).toDate().getFullYear());
				}
			});
		}
		return array;
	}

	private prepareGraphData(items, selectedPeriodicity, dateFrom) {
		// get x axis scale for selected period
		const scale = this.prepareScale(selectedPeriodicity, dateFrom, items);

		return scale.map((x) => {
			let total = 0;
			if (selectedPeriodicity === Periodicity.Week) {
				items.map((y) => {
					if (moment.utc(y.date).toDate().getDay() === x.getDay()) {
						total = total + y.total;
					}
				});
				return {
					x: moment(x).format('MMM') + ' ' + moment(x).format('D'),
					y: total,
				};
				// month days
			} else if (selectedPeriodicity === Periodicity.Month) {
				items.map((y) => {
					const dataDate = moment.utc(y.date).toDate();
					if (
						dataDate.getFullYear() === x.getFullYear() &&
						dataDate.getMonth() === x.getMonth() &&
						dataDate.getDate() === x.getDate()
					) {
						total = total + y.total;
					}
				});
				return {
					x: x.getDate(),
					y: total,
				};
				// months
			} else if (selectedPeriodicity === Periodicity.Year) {
				items.map((y) => {
					if (moment.utc(y.date).toDate().getMonth() === x) {
						total = total + y.total;
					}
				});
				return {
					x,
					y: total,
				};
			} else if (selectedPeriodicity === Periodicity.AllTime) {
				items.map((y) => {
					if (moment.utc(y.date).toDate().getFullYear() === x) {
						total = total + y.total;
					}
				});
				return {
					x,
					y: total,
				};
			}
		});
	}

	private changeTabAndGetRequiredData(selectedTab: string) {
		this.props.changeTabAndGetRequiredData(selectedTab, this.props.params.id);
	}

	public render() {
		if (typeof window === 'undefined') {
			return null;
		}

		const { id } = this.props.params;
		const { t, showModal, closeModal } = this.props;
		const {
			selectedPeriodicity,
			dateFrom,
			dateTo,
			sales,
			followers,
			isFetching,
			isFetchingFollowers,
			isFetchingOrders,
			orders,
			products,
			isFetchingProducts,
			selectedProductsTab,
		} = this.props.shopDashboardData;

		const salesData = [
			{
				id: 'Sales',
				color: 'hsl(278, 70%, 50%)',
				data: this.prepareGraphData(sales, selectedPeriodicity, dateFrom),
			},
		];

		const followersData = [
			{
				id: 'Followers',
				color: 'hsl(278, 70%, 50%)',
				data: this.prepareGraphData(followers, selectedPeriodicity, dateFrom),
			},
		];

		const ShopSales = ({ data }) => (
			<ResponsiveLine
				data={data}
				margin={{ top: 50, right: 50, bottom: 50, left: 50 }}
				xScale={{ type: 'point' }}
				yScale={{ type: 'linear', stacked: true, min: 'auto', max: 'auto' }}
				curve="monotoneX"
				axisTop={null}
				axisRight={null}
				axisBottom={{
					tickSize: 5,
					tickPadding: 5,
				}}
				axisLeft={null}
				colors="#fff8bd"
				lineWidth={0}
				pointSize={8}
				pointColor="#ffee5a"
				pointBorderColor={{ from: 'serieColor' }}
				enablePointLabel={true}
				pointLabel="y"
				pointLabelYOffset={-12}
				enableArea={true}
				areaOpacity={0.7}
				useMesh={true}
				isInteractive={false}
			/>
		);

		const ShopFollowers = ({ data }) => (
			<ResponsiveLine
				data={data}
				margin={{ top: 50, right: 50, bottom: 50, left: 50 }}
				xScale={{ type: 'point' }}
				yScale={{ type: 'linear', stacked: true, min: 'auto', max: 'auto' }}
				curve="monotoneX"
				axisTop={null}
				axisRight={null}
				axisBottom={{
					tickSize: 5,
					tickPadding: 5,
				}}
				axisLeft={null}
				colors="#fff8bd"
				lineWidth={0}
				pointSize={8}
				pointColor="#ffee5a"
				pointBorderColor={{ from: 'serieColor' }}
				enablePointLabel={true}
				pointLabel="y"
				pointLabelYOffset={-12}
				enableArea={true}
				areaOpacity={0.7}
				useMesh={true}
				isInteractive={false}
			/>
		);

		const periodSelector = (
			<PeriodSelector
				selectedPeriodicity={selectedPeriodicity}
				dateFrom={dateFrom}
				dateTo={dateTo}
				onPeriodicityChange={this.handlePeriodicityChange}
				setPeriodForward={this.handleOnForwardPeriodClick}
				setPeriodBackwards={this.handleOnBackwardsPeriodClick}
			/>
		);

		const dashboardOrdersProps = {
			orders,
			isFetchingOrders,
			shopId: id,
			showModal,
			closeModal,
			setStatus: this.props.setStatus,
		};

		const dashboardProductsProps = {
			products,
			isFetchingProducts,
			shopId: id,
			selectedTab: selectedProductsTab,
			changeTabAndGetRequiredData: this.changeTabAndGetRequiredData,
		};

		// TODO: translate currency
		return (
			<ManageShopWrapper id={id}>
				<div className="shopDashboardWrapper">
					<PageTitle title={t('Shop_Dashboard_Title')} titleContent={periodSelector} />
					<PortalPageSection title={t('Shop_Dashboard_Sales_Title')}>
						<div className="shopSaleFollowNumberWrapper">
							<span>{sales.map((x) => x.total).reduce((a, b) => a + b, 0)} EUR</span>
							{t('Shop_Dashboard_Sales_Sum')} {t('Periodselector_Periodicity_' + selectedPeriodicity)}
						</div>
						<LoadingOverlayWrapper
							active={isFetching}
							spinner={true}
							text={t('BSPortal_Common_Loading')}
							background="rgba(0, 0, 0, 0.1)"
						>
							<div style={{ height: '500px' }}>
								<ShopSales data={salesData} />
							</div>
						</LoadingOverlayWrapper>
					</PortalPageSection>
					<PortalPageSection title={t('Shop_Dashboard_Orders_Title')}>
						<DashboardOrders {...dashboardOrdersProps} />
					</PortalPageSection>
					<PortalPageSection title={t('Shop_Dashboard_Products_Title')}>
						<DashboardProducts {...dashboardProductsProps} />
					</PortalPageSection>
					<PortalPageSection title={t('Shop_Dashboard_Followers_Title')}>
						<div className="shopSaleFollowNumberWrapper">
							<span>{followers.map((x) => x.total).reduce((a, b) => a + b, 0)}</span>{' '}
							{t('Shop_Dashboard_Followers_Total')}
						</div>
						<LoadingOverlayWrapper
							active={isFetchingFollowers}
							spinner={true}
							text={t('BSPortal_Common_Loading')}
							background="rgba(0, 0, 0, 0.1)"
						>
							<div style={{ height: '500px' }}>
								<ShopFollowers data={followersData} />
							</div>
						</LoadingOverlayWrapper>
					</PortalPageSection>
				</div>
			</ManageShopWrapper>
		);
	}
}

export { ShopDashboard };
